import React, { FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { HubStats as SceneStats, STAR_BIG, STAR_LOW, STAR_TOP, compact } from './hubBrand'
import { PH_ARROW, PH_DUOTONE, PH_FOLDER, PH_LIGHT } from './phosphor'
import styles from './BriefFlowStage.module.css'
import { ColonyScene } from './ColonyScene'
import { prefersReducedMotion, useCountUp } from './countUp'

/*
 * Alternative Intelligence Hub banners (demo option). Both tell the same story with the team's real totals:
 * the stats flow into the AI, it thinks (Mission Control's animated gradient), and briefs for each stage of
 * the customer journey come out. The sequence is CSS animation (plus a count-up on the numbers), so the
 * banner's pause control (which pauses every animation inside it) and replay (which remounts this) work as
 * for the orbit scene. Each has its own look:
 *
 *  - pipeline ("Circuit"): monochrome, on a dotted pad. Thin traces with rounded corners run from each stat into
 *    the AI chip (which says what it's doing as it thinks), and out again to each brief, shown as a folder
 *  - horizon: dark, with colour only in the light behind the planet. Data drops onto it as dots, the light
 *    swells as it thinks, then rows of insights stream across it
 */

export type BriefFlowVariant = 'pipeline' | 'horizon' | 'colony'

/* a customer a live brief can open (demo) */
export interface FlowCustomer {
  id: string
  name: string
}

interface Props {
  variant: BriefFlowVariant
  stats?: SceneStats
  customerID?: string
  /** the team's customers: on Circuit, each live brief has a dropdown of them beside its name (demo) */
  customers?: FlowCustomer[]
  onOpen: (path: string) => void
  /** called when the sequence has played through */
  onDone: () => void
}

type StatKey = 'devices' | 'pages' | 'risk' | 'value'

interface Stat {
  key: StatKey
  label: string
  value?: number
  prefix?: string
}

interface Brief {
  key: 'setup' | 'health' | 'fleet' | 'renewal'
  name: string
  colour: string // the journey stage's colour, as on the Briefs tab (deepened to read on the light pad)
  path?: (id: string) => string
}

const statsOf = (s?: SceneStats): Stat[] => [
  { key: 'devices', label: 'Total devices', value: s?.devices },
  { key: 'pages', label: 'Pages last month', value: s?.printVolume },
  { key: 'risk', label: 'High-risk customers', value: s?.riskFlags },
  { key: 'value', label: 'Est. fleet value', value: s?.fleetValue, prefix: '$' },
]

/* one brief per journey stage: Onboarding, Serve, Upsell, Retention (two are live today) */
const BRIEFS: Brief[] = [
  { key: 'setup', name: 'Setup check', colour: '#039966' },
  { key: 'health', name: 'Health check', colour: '#1b9e8d', path: id => `/intelligence-hub/${id}/briefs/health-check` },
  { key: 'fleet', name: 'Fleet makeup', colour: '#3f72d9', path: id => `/intelligence-hub/${id}/briefs/fleet-makeup` },
  { key: 'renewal', name: 'Renewal maximizer', colour: '#5c6ac4' },
]

const StatValue: FunctionComponent<{
  stat: Stat
  delayMs: number
  x: number
  y: number
  className: string
}> = ({ stat, delayMs, x, y, className }) => {
  const n = useCountUp(stat.value, delayMs)
  return (
    <text x={x} y={y} className={className}>
      {n === undefined ? '—' : `${stat.prefix ?? ''}${compact(Math.round(n))}`}
    </text>
  )
}

/* a Phosphor icon centred on x, y */
const Icon: FunctionComponent<{
  name: StatKey | Brief['key']
  weight: 'light' | 'duotone'
  x: number
  y: number
  size: number
  colour: string
  className?: string
}> = ({ name, weight, x, y, size, colour, className }) => (
  <g transform={`translate(${x - size / 2} ${y - size / 2}) scale(${size / 256})`} fill={colour} className={className}>
    {(weight === 'light' ? PH_LIGHT : PH_DUOTONE)[name].map(p => (
      <path key={p.d.slice(0, 24)} d={p.d} opacity={p.faded ? 0.2 : 1} />
    ))}
  </g>
)

/* the Multiverse star: in its brand colours, or in one colour with each star a little lighter than the last */
const Star: FunctionComponent<{ x: number; y: number; size: number; colour?: string; brand?: boolean }> = ({
  x,
  y,
  size,
  colour = '#ffffff',
  brand = false,
}) => (
  <svg x={x - size / 2} y={y - size / 2} width={size} height={size} viewBox='-1 -1 48 48'>
    <path d={STAR_BIG} fill={brand ? '#27AA27' : colour} />
    {/* in colour, the stars take the AI gradient's green, teal and purple */}
    <path d={STAR_TOP} fill={brand ? '#32C2AF' : colour} fillOpacity={brand ? 1 : 0.75} />
    <path d={STAR_LOW} fill={brand ? '#AB87FF' : colour} fillOpacity={brand ? 1 : 0.55} />
  </svg>
)

/* a live brief links to the customer's copy of it */
const briefLink = (brief: Brief, customerID: string | undefined, onOpen: (path: string) => void) => {
  if (!brief.path || !customerID) return { linked: false, props: {} }
  const path = brief.path(customerID)
  return {
    linked: true,
    props: {
      tabIndex: 0,
      role: 'link',
      'aria-label': `Open ${brief.name}`,
      onClick: () => onOpen(path),
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen(path)
        }
      },
    },
  }
}

const BriefGroup: FunctionComponent<{
  brief: Brief
  index: number
  animation: string
  customerID?: string
  onOpen: (path: string) => void
  onDone?: () => void
}> = ({ brief, index, animation, customerID, onOpen, onDone, children }) => {
  const { linked, props } = briefLink(brief, customerID, onOpen)
  return (
    <g
      className={`${animation} ${styles[`i${index}`]}${linked ? ` ${styles.hit}` : ''}`}
      onAnimationEnd={onDone}
      {...props}
    >
      {children}
    </g>
  )
}

/* ---------- Circuit ---------- */

const CX = 600
const CY = 206
const ROWS = [78, 158, 254, 334] // a stat (left) and a brief (right) per row
const PORTS = [182, 198, 214, 230] // where the traces meet the chip's sides
const CHIP = 120
const CHIP_PAD = 32 // room around the chip so its shadow isn't clipped
const LINE_START = 238 // just past the stats
const LINE_END = 884 // just before the briefs
const R = 14 // trace corner radius
const ICON = 42 // stat icons and brief folders share a size, so their strokes match
const THOUGHTS = ['Reading fleet data…', 'Comparing print volume…', 'Weighing renewal risk…', 'Writing briefs…']

/* a trace: across, round the corner, along, round again, across */
const trace = (x0: number, y0: number, xTurn: number, y1: number, x1: number) => {
  const s = y1 > y0 ? 1 : -1
  const dir = x1 > x0 ? 1 : -1
  return (
    `M${x0} ${y0} H${xTurn - dir * R} Q${xTurn} ${y0} ${xTurn} ${y0 + s * R} V${y1 - s * R} ` +
    `Q${xTurn} ${y1} ${xTurn + dir * R} ${y1} H${x1}`
  )
}
const inTrace = (i: number) => trace(LINE_START, ROWS[i], [430, 400, 400, 430][i], PORTS[i], CX - CHIP / 2)
const outTrace = (i: number) => trace(CX + CHIP / 2, PORTS[i], [770, 800, 800, 770][i], ROWS[i], LINE_END)

const Circuit: FunctionComponent<Omit<Props, 'variant'>> = ({ stats, customerID, customers = [], onOpen, onDone }) => {
  const rows = statsOf(stats)
  return (
    <svg
      viewBox='0 0 1200 440'
      role='img'
      aria-label='Customer stats run into the AI, which writes a brief for each stage of the customer journey'
      className={styles.svg}
    >
      {/* stats, each at the start of its trace (the dotted pad is the banner's own background) */}
      {rows.map((s, i) => (
        <g key={s.key} className={`${styles.statIn} ${styles[`i${i}`]}`}>
          <Icon
            name={s.key}
            weight='light'
            x={58}
            y={ROWS[i] - 4}
            size={ICON}
            colour='#121e2b'
            className={styles.circuitIcon}
          />
          <StatValue stat={s} delayMs={300 + 200 * i} x={92} y={ROWS[i] + 4} className={styles.circuitValue} />
          <text x={92} y={ROWS[i] + 24} className={styles.circuitLabel}>
            {s.label}
          </text>
        </g>
      ))}

      {/* traces in: pale, with a dark pulse of data running along each */}
      {rows.map((s, i) => (
        <g key={`in-${s.key}`}>
          <circle cx={LINE_START} cy={ROWS[i]} r='4' className={`${styles.pad} ${styles[`i${i}`]}`} />
          <path d={inTrace(i)} pathLength={1} className={`${styles.trace} ${styles.keyIn} ${styles[`i${i}`]}`} />
          <path d={inTrace(i)} pathLength={1} className={`${styles.pulse} ${styles[`i${i}`]}`} />
        </g>
      ))}

      {/* the AI chip: a thin outline in Mission Control's spinning gradient; ripples spread while it thinks */}
      {[0, 1, 2].map(k => (
        <circle key={k} cx={CX} cy={CY} r='64' className={`${styles.ripple} ${styles[`r${k}`]}`} />
      ))}
      {/* a glassy panel like Mission Control's, pinned into the board */}
      {[-36, -12, 12, 36].map(dx => (
        <g key={dx} className={styles.pins}>
          <line x1={CX + dx} y1={CY - CHIP / 2 - 12} x2={CX + dx} y2={CY - CHIP / 2} />
          <line x1={CX + dx} y1={CY + CHIP / 2} x2={CX + dx} y2={CY + CHIP / 2 + 12} />
        </g>
      ))}
      <foreignObject
        x={CX - CHIP / 2 - CHIP_PAD}
        y={CY - CHIP / 2 - CHIP_PAD}
        width={CHIP + CHIP_PAD * 2}
        height={CHIP + CHIP_PAD * 2}
      >
        <div className={styles.chipFrame}>
          <div className={styles.chip} />
        </div>
      </foreignObject>
      <Star x={CX} y={CY} size={50} brand />

      {/* what it's doing, one step at a time */}
      {THOUGHTS.map((t, k) => (
        <text
          key={t}
          x={CX}
          y={CY + CHIP / 2 + 34}
          textAnchor='middle'
          className={`${styles.thought} ${styles[`t${k}`]}`}
        >
          {t}
        </text>
      ))}

      {/* traces out, one after another, each ending at its brief; the live ones are links and look it */}
      {BRIEFS.map((b, i) => {
        const live = Boolean(b.path && customerID)
        // with a customer dropdown under it, the dropdown opens the brief and the name is just a label
        const picker = live && customers.length > 0
        const linked = live && !picker
        const nameWidth = Math.round(b.name.length * 9.4)
        return (
          <g key={b.key}>
            <path d={outTrace(i)} pathLength={1} className={`${styles.traceOut} ${styles.keyOut} ${styles[`i${i}`]}`} />
            <BriefGroup
              brief={b}
              index={i}
              animation={styles.briefOut}
              customerID={linked ? customerID : undefined}
              onOpen={onOpen}
              onDone={i === BRIEFS.length - 1 ? onDone : undefined}
            >
              <g
                transform={`translate(${LINE_END + 4} ${ROWS[i] - ICON / 2}) scale(${ICON / 256})`}
                className={live ? styles.folder : styles.folderSoon}
              >
                <path d={PH_FOLDER} />
              </g>
              <text x={LINE_END + 58} y={ROWS[i] + 7} className={live ? styles.circuitBrief : styles.circuitBriefSoon}>
                {b.name}
              </text>
              {linked && (
                <>
                  <line
                    x1={LINE_END + 58}
                    y1={ROWS[i] + 12}
                    x2={LINE_END + 58 + nameWidth}
                    y2={ROWS[i] + 12}
                    className={styles.linkLine}
                  />
                  <g
                    transform={`translate(${LINE_END + 64 + nameWidth} ${ROWS[i] - 9}) scale(${16 / 256})`}
                    className={styles.linkArrow}
                  >
                    <path d={PH_ARROW} />
                  </g>
                </>
              )}
            </BriefGroup>
            {/* DEMO ONLY: under the name, open this brief for any customer: choosing one goes straight there */}
            {picker && b.path && (
              <foreignObject
                x={LINE_END + 58}
                y={ROWS[i] + 18}
                width={1196 - LINE_END - 58}
                height={36} // the dropdown is about 32px tall; room so its bottom edge isn't clipped
                className={`${styles.briefOut} ${styles[`i${i}`]}`}
              >
                <div className={`select sml ${styles.briefCustomer}`}>
                  <select
                    aria-label={`Open ${b.name} for a customer`}
                    value=''
                    onChange={e => e.target.value && b.path && onOpen(b.path(e.target.value))}
                  >
                    <option value='' disabled>
                      Choose a customer
                    </option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </foreignObject>
            )}
          </g>
        )
      })}
    </svg>
  )
}

/* ---------- Horizon ---------- */

const COLS = [240, 480, 720, 960]
const TOP = 240 // the planet's top edge
const PR = 900 // planet radius: wide enough that its sides leave through the bottom, not the banner's sides
const EDGE_Y = TOP + PR - Math.sqrt(PR * PR - CX * CX)
const RIM = `M0 ${EDGE_Y} A${PR} ${PR} 0 0 1 1200 ${EDGE_Y}`
const STAR_Y = TOP + 34

/* illustrative insights: real ones vary from brief to brief */
const INSIGHT_ROWS = [
  [
    'Print volume down 18%',
    '5 features underused',
    'Renewal in 60 days',
    'Colour pages up 9%',
    'Secure print off at 2 sites',
  ],
  [
    '12 devices near end of life',
    'Toner use spiking at HQ',
    'Mobile print unused',
    '3 sites over capacity',
    'Scan to cloud adopted',
  ],
  [
    'Support tickets halved',
    'Fleet value up $12k',
    'Duplex rate 41%',
    '2 printers offline 7+ days',
    'Badge release growing',
  ],
]
const INSIGHT_Y = [324, 364, 404]
const CHIP_H = 28
const CHIP_GAP = 10
const CHIP_PAD_X = 12 // the same space either side of the text
const estimateText = (text: string) => text.length * 6.1 // until the real width is measured

interface PlacedChip {
  text: string
  x: number
  w: number
}

/* lay a row out once; it's drawn twice, end to end, so it can scroll forever */
const layoutRow = (texts: string[], textWidths: Record<string, number>): { chips: PlacedChip[]; width: number } => {
  let x = 0
  const chips = texts.map(text => {
    const w = Math.round((textWidths[text] ?? estimateText(text)) + CHIP_PAD_X * 2)
    const chip = { text, x, w }
    x += w + CHIP_GAP
    return chip
  })
  return { chips, width: x }
}

const InsightChip: FunctionComponent<{ chip: PlacedChip; y: number; className: string; onEnd?: () => void }> = ({
  chip,
  y,
  className,
  onEnd,
}) => (
  // the chip's own entrance is its only animation
  <g className={className} onAnimationEnd={onEnd ? () => onEnd() : undefined}>
    <rect x={chip.x} y={y - CHIP_H / 2} width={chip.w} height={CHIP_H} rx={CHIP_H / 2} className={styles.insight} />
    <text x={chip.x + CHIP_PAD_X} y={y + 4.5} className={styles.insightText}>
      {chip.text}
    </text>
  </g>
)

/* the insight rows start scrolling as the first chips arrive, and stop after this long, so the banner settles */
const CHIPS_ARRIVE_MS = 5800 // the first chip's delay (.c00 in the CSS)
const SCROLL_MS = 10000

const Horizon: FunctionComponent<Omit<Props, 'variant'>> = ({ stats, onDone }) => {
  const [scrollStopped, setScrollStopped] = useState(false)
  useEffect(() => {
    const timer = window.setTimeout(() => setScrollStopped(true), CHIPS_ARRIVE_MS + SCROLL_MS)
    return () => window.clearTimeout(timer)
  }, [])
  const rows = statsOf(stats)
  // measure each insight's text once, so every chip fits its text with equal space either side
  const measureRef = useRef<SVGGElement>(null)
  const [textWidths, setTextWidths] = useState<Record<string, number>>({})
  useLayoutEffect(() => {
    const texts = measureRef.current?.querySelectorAll('text')
    if (!texts || texts.length === 0 || typeof texts[0].getComputedTextLength !== 'function') return
    const next: Record<string, number> = {}
    texts.forEach(t => {
      const w = t.getComputedTextLength()
      if (w > 0) next[t.textContent ?? ''] = w
    })
    if (Object.keys(next).length > 0) setTextWidths(next)
  }, [])
  const insightRows = INSIGHT_ROWS.map(row => layoutRow(row, textWidths))
  return (
    <svg
      viewBox='0 0 1200 440'
      role='img'
      aria-label='Customer stats drop into the AI planet, which turns them into insights'
      className={styles.svg}
    >
      <defs>
        <radialGradient id='bf-planet' cx='.5' cy='0' r='.6'>
          <stop offset='0' stopColor='#17151f' />
          <stop offset='.3' stopColor='#0b0b10' />
          <stop offset='1' stopColor='#070709' />
        </radialGradient>
        {/* the rim catches the light at the top and fades out down the sides */}
        <linearGradient id='bf-rim' x1='0' y1='0' x2='1200' y2='0' gradientUnits='userSpaceOnUse'>
          <stop offset='0' stopColor='#6f7dff' stopOpacity='0' />
          <stop offset='.3' stopColor='#6f7dff' stopOpacity='.45' />
          <stop offset='.55' stopColor='#ff9ccf' stopOpacity='.85' />
          <stop offset='.75' stopColor='#b07cff' stopOpacity='.45' />
          <stop offset='1' stopColor='#b07cff' stopOpacity='0' />
        </linearGradient>
        <clipPath id='bf-planet-clip'>
          <circle cx={CX} cy={TOP + PR} r={PR} />
        </clipPath>
        {/* a filter region covering the whole banner and more, so the glow never shows a hard edge */}
        <linearGradient id='bf-aura-fade-g' x1='0' y1='0' x2='1200' y2='0' gradientUnits='userSpaceOnUse'>
          <stop offset='0' stopColor='#fff' stopOpacity='0' />
          <stop offset='.18' stopColor='#fff' stopOpacity='1' />
          <stop offset='.82' stopColor='#fff' stopOpacity='1' />
          <stop offset='1' stopColor='#fff' stopOpacity='0' />
        </linearGradient>
        <mask id='bf-aura-fade' maskUnits='userSpaceOnUse' x='0' y='0' width='1200' height='440'>
          <rect width='1200' height='440' fill='url(#bf-aura-fade-g)' />
        </mask>
        <filter id='bf-aura-blur' filterUnits='userSpaceOnUse' x='-400' y='-400' width='2000' height='1240'>
          <feGaussianBlur stdDeviation='34' />
        </filter>
        <filter id='bf-star-glow' filterUnits='userSpaceOnUse' x={CX - 120} y={STAR_Y - 80} width='240' height='160'>
          <feGaussianBlur stdDeviation='7' />
        </filter>
        <filter id='bf-glint-blur' filterUnits='userSpaceOnUse' x='-100' y={TOP - 100} width='1400' height='500'>
          <feGaussianBlur stdDeviation='6' />
        </filter>
      </defs>

      {/* hidden: used to measure the insights' text */}
      <g ref={measureRef} className={styles.measure} aria-hidden='true'>
        {INSIGHT_ROWS.flat().map(text => (
          <text key={text} className={styles.insightText}>
            {text}
          </text>
        ))}
      </g>

      {/* light from behind the planet, hugging its rim, spread wide and fading out before the banner's sides */}
      <g mask='url(#bf-aura-fade)'>
        <g filter='url(#bf-aura-blur)' className={styles.aura}>
          <ellipse cx='330' cy={TOP + 58} rx='330' ry='60' fill='#3b4ef0' />
          <ellipse cx='600' cy={TOP + 26} rx='460' ry='70' fill='#8f5bd9' />
          <ellipse cx='870' cy={TOP + 54} rx='330' ry='60' fill='#f06aa8' />
        </g>
      </g>

      {/* the planet: dark, with a thin lit rim; while the AI thinks a glint runs along its edge */}
      <circle cx={CX} cy={TOP + PR} r={PR} fill='url(#bf-planet)' />
      <path d={RIM} fill='none' stroke='url(#bf-rim)' strokeWidth='1.5' />
      <path d={RIM} pathLength={1} filter='url(#bf-glint-blur)' className={styles.glintGlow} />
      <path d={RIM} pathLength={1} className={styles.glint} />

      {/* the AI's stars, just inside the rim */}
      <g filter='url(#bf-star-glow)' opacity='.6'>
        <Star x={CX} y={STAR_Y} size={36} />
      </g>
      <Star x={CX} y={STAR_Y} size={36} />

      {/* stats above, counting up, each with its icon beside the number */}
      {rows.map((s, i) => {
        const x = COLS[i] - 80
        return (
          <g key={s.key} className={`${styles.statDown} ${styles[`i${i}`]}`}>
            <text x={x} y='70' className={styles.statLabel}>
              {s.label}
            </text>
            <Icon name={s.key} weight='light' x={x + 16} y={103} size={32} colour='#e6e9f5' />
            <StatValue stat={s} delayMs={400 + 200 * i} x={x + 42} y={118} className={styles.horizonValue} />
          </g>
        )
      })}

      {/* each stat drops onto the planet as a few dots */}
      {COLS.map((x, i) => {
        const d = `M${x} 140 C${x} 190 ${CX} 184 ${CX} ${TOP - 8}`
        return (
          <g key={`drop${x}`}>
            <path d={d} pathLength={1} className={`${styles.thread} ${styles.keyIn} ${styles[`i${i}`]}`} />
            <path d={d} pathLength={1} className={`${styles.dots} ${styles[`i${i}`]}`} />
          </g>
        )
      })}

      {/* once it has thought, insights stream across the planet: three rows at different speeds, cropped by it */}
      <g clipPath='url(#bf-planet-clip)'>
        {insightRows.map((row, r) => (
          <g key={r} transform='translate(140 0)'>
            <g
              className={`${styles.insightRow} ${styles[`row${r}`]}${scrollStopped ? ` ${styles.scrollStopped}` : ''}`}
            >
              {/* spans exactly two copies, so scrolling by half loops seamlessly */}
              <rect x='0' y={INSIGHT_Y[r] - CHIP_H / 2} width={row.width * 2} height={CHIP_H} fill='none' />
              {[0, 1].map(copy =>
                row.chips.map((chip, j) => (
                  <InsightChip
                    key={`${copy}-${chip.text}`}
                    chip={{ ...chip, x: chip.x + copy * row.width }}
                    y={INSIGHT_Y[r]}
                    className={`${styles.chipIn} ${styles[`c${r}${j}`]}`}
                    onEnd={r === 2 && copy === 0 && j === row.chips.length - 1 ? onDone : undefined}
                  />
                ))
              )}
            </g>
          </g>
        ))}
      </g>
    </svg>
  )
}

export const BriefFlowStage: FunctionComponent<Props> = ({ variant, ...rest }) => {
  const { onDone } = rest
  // with reduced motion everything shows at once, so the sequence is done straight away
  useEffect(() => {
    if (prefersReducedMotion()) onDone()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <div className={styles.root} data-testid={`brief-flow-${variant}`}>
      {variant === 'pipeline' && <Circuit {...rest} />}
      {variant === 'horizon' && <Horizon {...rest} />}
      {variant === 'colony' && <ColonyScene stats={rest.stats} onDone={rest.onDone} />}
    </div>
  )
}
